import {
  ChangeEventHandler,
  DetailedHTMLProps,
  FormEventHandler,
  ForwardedRef,
  forwardRef,
  InputHTMLAttributes,
  useState,
} from "react"
import './Input.less'
import indefiniteArticlise from 'indefinite'
import ValidationMessage from "../ValidationMessage/ValidationMessage"

interface CustomProps {
  fieldName?: string // the name that will be used in validation error messages
  forwardedRef?: ForwardedRef<HTMLInputElement | null>
}

type InputProps = DetailedHTMLProps<
    InputHTMLAttributes<HTMLInputElement>, HTMLInputElement
  > & CustomProps

const capitalise = (name: string): string => `${name[0].toUpperCase()}${name.slice(1)}`

export const nameToIndefiniteArticle = (name: string, capitalise?: boolean): string => (
  indefiniteArticlise(name, { capitalize: capitalise })
)

const getValueMissingMessage = (fieldName: string): string => (
  `Please enter ${nameToIndefiniteArticle(fieldName)}`
)
const getRangeUnderflowMessage = (fieldName: string, min: string): string => (
  `${capitalise(fieldName)} must be greater than ${min}`
)
const getRangeOverflowMessage = (fieldName: string, max: string): string => (
  `${capitalise(fieldName)} must be less than ${max}`
)
const getPatternMismatchMessage = (fieldName: string): string => (
  `${capitalise(fieldName)} doesn't match the expected format`
)
const getMinLengthMessage = (fieldName: string, minLength: number): string => (
  `${capitalise(fieldName)} must be longer than ${minLength} characters`
)
const getMaxLengthMessage = (fieldName: string, maxLength: number): string => (
  `${capitalise(fieldName)} must be shorter than ${maxLength} characters`
)
const getStepMismatchMessage = (fieldName: string, step: string) => (
  step === '1'
  ?
  `${capitalise(fieldName)} must be a whole number`
  :
  `${capitalise(fieldName)} must be a multiple of ${step}`
)
const getInvalidInputMessage = (fieldName: string): string => (
  `Please enter a valid ${fieldName}`
)

const getValidationMessage = (
    input: EventTarget & HTMLInputElement, fieldName: string
  ): string | null => {
    if (input.validity.valid) return null
    if (input.validity.valueMissing) return getValueMissingMessage(fieldName)
    if (input.validity.rangeUnderflow) return getRangeUnderflowMessage(fieldName, input.min)
    if (input.validity.rangeOverflow) return getRangeOverflowMessage(fieldName, input.max)
    if (input.validity.tooLong) return getMaxLengthMessage(fieldName, input.maxLength)
    if (input.validity.tooShort) return getMinLengthMessage(fieldName, input.minLength)
    if (input.validity.customError) return input.validationMessage
    if (input.validity.patternMismatch) return getPatternMismatchMessage(fieldName)
    if (input.validity.stepMismatch) return getStepMismatchMessage(fieldName, input.step)
    if (input.validity.badInput) return getInvalidInputMessage(fieldName)
    if (input.validity.typeMismatch) return getInvalidInputMessage(fieldName)

    return getInvalidInputMessage(fieldName)
  }



const _Input = ({
    onChange: _onChange,
    onInvalid: _onInvalid,
    className,
    fieldName = 'value',
    forwardedRef,
    ...props
  }: InputProps): JSX.Element => {
  const [invalidMessage, setInvalidMessage] = useState<string | null>(null)

  const onInvalid: FormEventHandler<HTMLInputElement> = (event) => {
    _onInvalid && _onInvalid(event)
    const { currentTarget } = event
    setInvalidMessage(getValidationMessage(currentTarget, fieldName))
    event.preventDefault()
  }
  const onChange: ChangeEventHandler<HTMLInputElement> = (event) => {
    _onChange && _onChange(event)
    setInvalidMessage(null)
  }

  return (
    <ValidationMessage className='hb-input' invalidMessage={invalidMessage}>
      <input
        {...props}
        onInvalid={onInvalid}
        onChange={onChange}
        ref={forwardedRef}
        className={`${className ? `${className} ` : ''}`}
        />
    </ValidationMessage>
  )
}

const Input = forwardRef<HTMLInputElement | null, InputProps>(
  function CustomInput(props, ref) {
   return <_Input forwardedRef={ref} {...props} />
  })

export default Input
